import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';
import { createNewsUrl } from '../../utils/urlUtils';
import { useAuth } from '../../hooks/useAuth';
import ShareToMessageModal, { SharedContent } from '../../components/base/ShareToMessageModal';
import { supabase } from '../../lib/supabase';

const newsCategories = [
  { id: 'all', name: 'All News', icon: 'ri-newspaper-line', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  { id: 'production', name: 'Production', icon: 'ri-film-line', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  { id: 'streaming', name: 'Streaming', icon: 'ri-live-line', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  { id: 'awards', name: 'Awards', icon: 'ri-award-line', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  { id: 'casting', name: 'Casting', icon: 'ri-user-star-line', color: 'text-rose-600', bgColor: 'bg-rose-100' },
  { id: 'technology', name: 'Technology', icon: 'ri-cpu-line', color: 'text-teal-600', bgColor: 'bg-teal-100' },
  { id: 'industry', name: 'Industry', icon: 'ri-building-line', color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
];

const archiveMonths = [
  { month: 'January 2025', count: 12 },
  { month: 'December 2024', count: 28 },
  { month: 'November 2024', count: 24 },
  { month: 'October 2024', count: 31 },
  { month: 'September 2024', count: 22 },
  { month: 'August 2024', count: 19 },
];

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  image: string;
  author_name: string;
  author_avatar: string;
  published_at: string;
  read_time: number;
  is_breaking: boolean;
  is_featured: boolean;
  views: number;
  tags: string[];
  ai_summary: any;
  status: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

const getCategoryInfo = (categoryId: string) => {
  return newsCategories.find(c => c.id === categoryId) || newsCategories[0];
};

// Share Dropdown Component
interface ShareDropdownProps {
  title: string;
  url: string;
  isOpen: boolean;
  onClose: () => void;
  onShareToChat?: () => void;
  isLoggedIn?: boolean;
}

const ShareDropdown = ({ title, url, isOpen, onClose, onShareToChat, isLoggedIn }: ShareDropdownProps) => {
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShare = (e: React.MouseEvent, shareUrl: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(shareUrl, '_blank', 'width=600,height=400');
    onClose();
  };

  const handleShareToChat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onShareToChat?.();
    onClose();
  };

  if (!isOpen) return null;

  const shareOptions = [
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-line',
      color: 'text-black',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    },
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'text-blue-600',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      color: 'text-blue-700',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-fill',
      color: 'text-green-600',
      url: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    },
  ];

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 top-full mt-1 z-30 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-44 animate-in fade-in slide-in-from-top-1 duration-150"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
        Share to
      </div>
      <div className="grid grid-cols-4 gap-1 px-2 py-2">
        {shareOptions.map((option) => (
          <button
            key={option.name}
            onClick={(e) => handleShare(e, option.url)}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            title={option.name}
          >
            <i className={`${option.icon} ${option.color} text-lg`}></i>
          </button>
        ))}
      </div>
      {isLoggedIn && (
        <div className="border-t border-gray-100 mt-1 pt-1">
          <button
            onClick={handleShareToChat}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-colors cursor-pointer"
          >
            <i className="ri-chat-forward-line text-purple-500"></i>
            <span>Send to Chat</span>
          </button>
        </div>
      )}
      <div className={`${isLoggedIn ? '' : 'border-t border-gray-100 mt-1 pt-1'}`}>
        <button
          onClick={handleCopyLink}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <i className={`${copied ? 'ri-check-line text-green-500' : 'ri-link text-gray-400'}`}></i>
          <span>{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>
      </div>
    </div>
  );
};

// Article Share Button Component
interface ArticleShareButtonProps {
  title: string;
  articleId: string | number;
  excerpt: string;
  image: string;
  category: string;
  categoryColor?: string;
  onShareToChat: (content: SharedContent) => void;
  isLoggedIn: boolean;
}

const ArticleShareButton = ({ title, articleId, excerpt, image, category, categoryColor, onShareToChat, isLoggedIn }: ArticleShareButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const fullUrl = `${window.location.origin}/news/${articleId}`;

  const handleShareToChat = () => {
    onShareToChat({
      type: 'post',
      id: articleId.toString(),
      title,
      description: excerpt,
      image,
      url: fullUrl,
      meta: {
        category,
        categoryColor
      }
    });
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
        title="Share article"
      >
        <i className="ri-share-line text-sm"></i>
      </button>
      <ShareDropdown
        title={title}
        url={fullUrl}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onShareToChat={handleShareToChat}
        isLoggedIn={isLoggedIn}
      />
    </div>
  );
};

export default function NewsArchivePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { user } = useAuth();
  
  // Database state
  const [allArticles, setAllArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Share to chat modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareContent, setShareContent] = useState<SharedContent | null>(null);
  
  // Pagination state
  const itemsPerPage = 6;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Fetch articles from database
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('news_articles')
          .select('*')
          .eq('status', 'published')
          .order('published_at', { ascending: false });
        
        if (error) throw error;
        setAllArticles(data || []);
      } catch (err) {
        console.error('Error fetching news articles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const filteredArticles = useMemo(() => {
    let articles = activeCategory === 'all' 
      ? allArticles 
      : allArticles.filter(a => a.category === activeCategory);
    
    // Search filter
    if (searchTerm) {
      articles = articles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Sort
    switch (sortBy) {
      case 'popular':
        articles = [...articles].sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      case 'oldest':
        articles = [...articles].sort((a, b) => 
          new Date(a.published_at).getTime() - new Date(b.published_at).getTime()
        );
        break;
      default:
        articles = [...articles].sort((a, b) => 
          new Date(b.published_at).getTime() - new Date(a.published_at).getTime()
        );
    }
    
    return articles;
  }, [activeCategory, sortBy, searchTerm, allArticles]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedArticles = filteredArticles.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    const newParams = new URLSearchParams(searchParams);
    if (page === 1) {
      newParams.delete('page');
    } else {
      newParams.set('page', page.toString());
    }
    setSearchParams(newParams);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const featuredArticle = useMemo(() => {
    return allArticles.find(a => a.is_featured) || allArticles[0];
  }, [allArticles]);

  const popularTags = useMemo(() => {
    const tagCounts: Record<string, number> = {};
    allArticles.forEach(article => {
      article.tags?.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([tag, count]) => ({ tag, count }));
  }, [allArticles]);

  // Get "You May Also Like" articles - mix of popular and recent
  const youMayAlsoLike = useMemo(() => {
    const sorted = [...allArticles]
      .sort((a, b) => {
        const aDate = new Date(a.published_at).getTime();
        const bDate = new Date(b.published_at).getTime();
        const now = Date.now();
        const aRecency = 1 - (now - aDate) / (1000 * 60 * 60 * 24 * 30);
        const bRecency = 1 - (now - bDate) / (1000 * 60 * 60 * 24 * 30);
        const aScore = ((a.views || 0) / 1000) + (aRecency * 10) + (a.is_featured ? 5 : 0);
        const bScore = ((b.views || 0) / 1000) + (bRecency * 10) + (b.is_featured ? 5 : 0);
        return bScore - aScore;
      })
      .slice(0, 5);
    return sorted;
  }, [allArticles]);

  const handleOpenShareModal = (content: SharedContent) => {
    setShareContent(content);
    setShareModalOpen(true);
  };

  const handleSendToConversation = async (conversationId: string, content: SharedContent) => {
    if (!user) return;
    
    const sharedMessage = {
      type: 'shared_content',
      content: {
        type: content.type,
        id: content.id,
        title: content.title,
        description: content.description,
        image: content.image,
        url: content.url,
        meta: content.meta
      }
    };

    await supabase.from('messages').insert({
      conversation_id: conversationId,
      sender_id: user.id,
      content: JSON.stringify(sharedMessage)
    });

    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId);
  };

  const handleStartNewConversation = async (userId: string, content: SharedContent) => {
    if (!user) return;

    // Check for existing conversation
    const { data: myConvs } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', user.id);

    const { data: theirConvs } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', userId);

    const myConvIds = myConvs?.map(c => c.conversation_id) || [];
    const theirConvIds = theirConvs?.map(c => c.conversation_id) || [];
    const commonConvId = myConvIds.find(id => theirConvIds.includes(id));

    let conversationId = commonConvId;

    if (!conversationId) {
      const { data: newConv } = await supabase
        .from('conversations')
        .insert({ is_group: false })
        .select()
        .single();

      if (newConv) {
        conversationId = newConv.id;
        await supabase.from('conversation_participants').insert([
          { conversation_id: conversationId, user_id: user.id },
          { conversation_id: conversationId, user_id: userId }
        ]);
      }
    }

    if (conversationId) {
      await handleSendToConversation(conversationId, content);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="lg:w-1/2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <i className="ri-newspaper-line text-white text-lg"></i>
                </div>
                <span className="text-amber-400 font-medium">Industry News</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                News <span className="text-amber-400">Archive</span>
              </h1>
              <p className="text-gray-300 text-lg mb-6">
                Stay updated with the latest developments in the Arab drama industry. From production announcements to award ceremonies, we cover it all.
              </p>
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <i className="ri-article-line"></i>
                  {allArticles.length} Articles
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-folder-line"></i>
                  {newsCategories.length - 1} Categories
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-refresh-line"></i>
                  Updated Daily
                </span>
              </div>
            </div>
            
            {/* Featured Article Preview */}
            {featuredArticle && !loading && (
              <div className="lg:w-1/2">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-full">
                      Featured
                    </span>
                    {featuredArticle.is_breaking && (
                      <span className="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                        <i className="ri-alarm-warning-line"></i>
                        Breaking
                      </span>
                    )}
                  </div>
                  <Link to={createNewsUrl(featuredArticle.id, featuredArticle.title, featuredArticle.slug)} className="group">
                    <img 
                      src={featuredArticle.image} 
                      alt={featuredArticle.title}
                      className="w-full h-40 object-cover object-top rounded-lg mb-3 group-hover:opacity-90 transition-opacity"
                    />
                    <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">
                      {featuredArticle.title}
                    </h3>
                  </Link>
                  <p className="text-gray-300 text-sm line-clamp-2 mb-3">{featuredArticle.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{formatTimeAgo(featuredArticle.published_at)}</span>
                    <span>{featuredArticle.read_time} min read</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Filter Strip */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 gap-4">
            {/* Category Pills */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
              {newsCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className={category.icon}></i>
                  {category.name}
                </button>
              ))}
            </div>
            
            {/* Controls */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="relative hidden sm:block">
                <input
                  type="text"
                  placeholder="Search news..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm w-48"
                />
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
              
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm cursor-pointer"
              >
                <option value="latest">Latest</option>
                <option value="popular">Popular</option>
                <option value="oldest">Oldest</option>
              </select>
              
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 cursor-pointer transition-colors ${
                    viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-grid-line"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 cursor-pointer transition-colors ${
                    viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-list-check"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="sm:hidden bg-white border-b border-gray-200 px-4 py-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search news..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
          />
          <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Articles Grid/List */}
          <div className="flex-1">
            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600 text-sm">
                {loading ? 'Loading articles...' : (
                  <>
                    Showing <span className="font-medium text-gray-900">{filteredArticles.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredArticles.length)}</span> of <span className="font-medium text-gray-900">{filteredArticles.length}</span> articles
                    {activeCategory !== 'all' && (
                      <span> in <span className="font-medium text-gray-900">{getCategoryInfo(activeCategory).name}</span></span>
                    )}
                  </>
                )}
              </p>
            </div>

            {loading ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <i className="ri-loader-4-line animate-spin text-4xl text-amber-500 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading articles...</h3>
                <p className="text-gray-600">Fetching the latest news for you.</p>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <i className="ri-newspaper-line text-5xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paginatedArticles.map((article) => {
                  const categoryInfo = getCategoryInfo(article.category);
                  const articleUrl = createNewsUrl(article.id, article.title, article.slug);
                  return (
                    <article 
                      key={article.id} 
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
                    >
                      <Link to={articleUrl} className="block">
                        <div className="relative">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute top-3 left-3 flex items-center gap-2">
                            <span className={`px-2 py-1 ${categoryInfo.bgColor} ${categoryInfo.color} text-xs font-medium rounded-full`}>
                              {categoryInfo.name}
                            </span>
                            {article.is_breaking && (
                              <span className="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                                <i className="ri-alarm-warning-line"></i>
                                Breaking
                              </span>
                            )}
                          </div>
                          {/* AI Summary Badge */}
                          {article.ai_summary && (
                            <div className="absolute bottom-3 left-3">
                              <span className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-full flex items-center gap-1 shadow-sm">
                                <i className="ri-sparkling-2-fill text-[10px]"></i>
                                AI Summary
                              </span>
                            </div>
                          )}
                        </div>
                      </Link>
                      
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{formatDate(article.published_at)}</span>
                            <span>•</span>
                            <span>{article.read_time} min read</span>
                          </div>
                          <ArticleShareButton 
                            title={article.title} 
                            articleId={article.slug}
                            excerpt={article.excerpt}
                            image={article.image}
                            category={categoryInfo.name}
                            categoryColor={categoryInfo.color.replace('text-', '').includes('amber') ? '#d97706' : categoryInfo.color.replace('text-', '').includes('blue') ? '#2563eb' : categoryInfo.color.replace('text-', '').includes('green') ? '#16a34a' : categoryInfo.color.replace('text-', '').includes('purple') ? '#9333ea' : categoryInfo.color.replace('text-', '').includes('rose') ? '#e11d48' : '#6b7280'}
                            onShareToChat={handleOpenShareModal}
                            isLoggedIn={!!user}
                          />
                        </div>
                        
                        <Link to={articleUrl}>
                          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">
                            {article.title}
                          </h3>
                        </Link>
                        
                        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {article.author_avatar && (
                              <img 
                                src={article.author_avatar} 
                                alt={article.author_name}
                                className="w-7 h-7 rounded-full object-cover"
                              />
                            )}
                            <span className="text-sm text-gray-700">{article.author_name}</span>
                          </div>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {(article.views || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              /* List View */
              <div className="space-y-4">
                {paginatedArticles.map((article) => {
                  const categoryInfo = getCategoryInfo(article.category);
                  const articleUrl = createNewsUrl(article.id, article.title, article.slug);
                  return (
                    <article 
                      key={article.id} 
                      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow group"
                    >
                      <div className="flex gap-4">
                        <Link to={articleUrl} className="flex-shrink-0 relative">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-32 h-24 sm:w-48 sm:h-32 object-cover object-top rounded-lg group-hover:opacity-90 transition-opacity"
                          />
                          {/* AI Summary Badge for List View */}
                          {article.ai_summary && (
                            <div className="absolute bottom-2 left-2">
                              <span className="px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-medium rounded-full flex items-center gap-0.5 shadow-sm">
                                <i className="ri-sparkling-2-fill text-[8px]"></i>
                                AI
                              </span>
                            </div>
                          )}
                        </Link>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 ${categoryInfo.bgColor} ${categoryInfo.color} text-xs font-medium rounded-full`}>
                                {categoryInfo.name}
                              </span>
                              {article.is_breaking && (
                                <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-medium rounded-full">
                                  Breaking
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{formatTimeAgo(article.published_at)}</span>
                            </div>
                            <ArticleShareButton 
                              title={article.title} 
                              articleId={article.slug}
                              excerpt={article.excerpt}
                              image={article.image}
                              category={categoryInfo.name}
                              categoryColor={categoryInfo.color.replace('text-', '').includes('amber') ? '#d97706' : categoryInfo.color.replace('text-', '').includes('blue') ? '#2563eb' : categoryInfo.color.replace('text-', '').includes('green') ? '#16a34a' : categoryInfo.color.replace('text-', '').includes('purple') ? '#9333ea' : categoryInfo.color.replace('text-', '').includes('rose') ? '#e11d48' : '#6b7280'}
                              onShareToChat={handleOpenShareModal}
                              isLoggedIn={!!user}
                            />
                          </div>
                          
                          <Link to={articleUrl}>
                            <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">
                              {article.title}
                            </h3>
                          </Link>
                          
                          <p className="text-gray-600 text-sm line-clamp-2 mb-3 hidden sm:block">
                            {article.excerpt}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {article.author_avatar && (
                                <img 
                                  src={article.author_avatar} 
                                  alt={article.author_name}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              )}
                              <span className="text-sm text-gray-700">{article.author_name}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                {article.read_time} min
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-eye-line"></i>
                                {(article.views || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {filteredArticles.length > 0 && totalPages > 1 && (
              <div className="mt-10">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Page Info */}
                  <p className="text-sm text-gray-600 order-2 sm:order-1">
                    Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                  </p>
                  
                  {/* Pagination Controls */}
                  <div className="flex items-center gap-1 order-1 sm:order-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors whitespace-nowrap ${
                        currentPage === 1
                          ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                      }`}
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="w-10 h-10 flex items-center justify-center text-gray-400">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page as number)}
                            className={`w-10 h-10 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              currentPage === page
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors whitespace-nowrap ${
                        currentPage === totalPages
                          ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                      }`}
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>
                  </div>
                  
                  {/* Items Per Page Info */}
                  <p className="text-sm text-gray-600 order-3">
                    {itemsPerPage} per page
                  </p>
                </div>
              </div>
            )}

            {/* Single Page Info */}
            {filteredArticles.length > 0 && totalPages === 1 && (
              <div className="text-center mt-10">
                <p className="text-sm text-gray-500">
                  Showing all {filteredArticles.length} articles
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* You May Also Like */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-sparkling-line text-amber-500"></i>
                You May Also Like
              </h3>
              <div className="space-y-4">
                {youMayAlsoLike.map((article, index) => {
                  const categoryInfo = getCategoryInfo(article.category);
                  return (
                    <Link
                      key={article.id}
                      to={createNewsUrl(article.id, article.title, article.slug)}
                      className="group flex gap-3"
                    >
                      <div className="relative flex-shrink-0">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-20 h-16 object-cover object-top rounded-lg group-hover:opacity-90 transition-opacity"
                        />
                        {index === 0 && (
                          <div className="absolute -top-1 -left-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <i className="ri-fire-fill text-white text-xs"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-amber-600 transition-colors leading-tight">
                          {article.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`px-1.5 py-0.5 ${categoryInfo.bgColor} ${categoryInfo.color} text-[10px] font-medium rounded`}>
                            {categoryInfo.name}
                          </span>
                          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                            <i className="ri-eye-line"></i>
                            {(article.views || 0) >= 1000 ? `${((article.views || 0) / 1000).toFixed(1)}k` : article.views}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <Link
                to="/news"
                onClick={() => {
                  handleSortChange('popular');
                  window.scrollTo({ top: 400, behavior: 'smooth' });
                }}
                className="mt-4 flex items-center justify-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
              >
                View All Popular
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Archive Timeline */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-calendar-line text-amber-500"></i>
                Archive
              </h3>
              <div className="space-y-2">
                {archiveMonths.map((item, index) => (
                  <button
                    key={item.month}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                      index === 0 
                        ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-500' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>{item.month}</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                      {item.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Tags */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-hashtag text-amber-500"></i>
                Popular Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularTags.map(({ tag, count }) => (
                  <button
                    key={tag}
                    onClick={() => handleSearchChange(tag)}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-amber-100 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    #{tag}
                    <span className="ml-1 text-xs text-gray-500">({count})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <i className="ri-mail-line"></i>
                Stay Updated
              </h3>
              <p className="text-sm text-amber-100 mb-4">
                Get the latest news delivered to your inbox weekly.
              </p>
              <div className="space-y-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 placeholder-white/70 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <button className="w-full px-4 py-2 bg-white text-amber-600 rounded-lg font-medium text-sm hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-amber-500"></i>
                Quick Links
              </h3>
              <div className="space-y-2">
                <Link 
                  to="/forum?tab=news" 
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  <i className="ri-discuss-line"></i>
                  Discuss News in Forum
                </Link>
                <Link 
                  to="/dramas" 
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  <i className="ri-film-line"></i>
                  Browse Dramas
                </Link>
                <Link 
                  to="/creators" 
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  <i className="ri-user-star-line"></i>
                  Discover Creators
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Share to Message Modal */}
      <ShareToMessageModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        content={shareContent}
        onSend={handleSendToConversation}
        onStartNewConversation={handleStartNewConversation}
      />
    </div>
  );
}
